import Preferences from 'context/preferences';
import { hasFullScope } from '../../common/scope';

/**
 * paneWidths.js — where Mission Control's two draggable panes and its board view are remembered, and
 * the one function that decides a width.
 *
 * Browser-local and blob-scoped: a pane width is a property of the person reading, for one body of
 * work. Server-side persistence is a recorded follow-up.
 */

export const RAIL_WIDTH_KEY = 'blob:workflow.missionControl.railWidth';
export const PANEL_WIDTH_KEY = 'blob:workflow.missionControl.panelWidth';
export const BOARD_VIEW_KEY = 'blob:workflow.missionControl.boardView';

// First is the default, and a stored value outside this list is read as the default — a build that
// has never heard of a view cannot draw it.
export const BOARD_VIEWS = ['graph', 'board'];

// The board view was stored as 'columns' before it was renamed; a reader who chose it keeps it.
const LEGACY_BOARD_VIEWS = { columns: 'board' };

// A pane narrower than this says nothing, and a board narrower than this is not a board. Only
// clampPaneWidth reads them.
// MIN_PANE constrains a DRAG, not the layout: flex shrinks panes in proportion to their basis, so a rail can
// render below it (151px measured) when the dock is wider than the room. No hard min-width on the rail, on
// purpose: the board floor is the one guarantee needed, and a rail floor would trade narrowing for scrolling.
export const MIN_PANE = 180;
export const MIN_BOARD = 240;
// One arrow key's worth — the same 16px the layout's own gaps are built from, so a nudged pane lands
// on the grid the cards already sit on.
export const STEP = 16;

/**
 * The width to use, or null when there is none to use.
 *
 * Null is a real answer and the caller's cue to let the stylesheet decide: it is what a pane nobody
 * has dragged reads, and what a corrupt localStorage entry reads. Returning a number for either is
 * how `NaN`px reaches a style attribute.
 * @param {*} width
 * @param {{ min?: number, max?: number }} bounds
 * @returns {number|null}
 */
export function clampPaneWidth(width, bounds = {}) {
  const value = typeof width === 'number' ? width : Number(width);
  if (!Number.isFinite(value) || value <= 0) return null;
  // A NaN bound would make Math.max/min return NaN, so a bad bound falls back to the module's own.
  const min = Number.isFinite(bounds.min) ? bounds.min : MIN_PANE;
  const max = Number.isFinite(bounds.max) || bounds.max === Infinity ? bounds.max : Infinity;
  // A container too narrow for both minimums has no answer that satisfies both. The pane's floor
  // wins; flex-shrink is what gives the board its room back, not arithmetic here.
  return Math.round(Math.min(Math.max(value, min), Math.max(min, max)));
}

export function readPaneWidth(scope, key) {
  if (!hasFullScope(scope)) return null;
  return clampPaneWidth(Preferences.get(key, scope));
}

export function writePaneWidth(scope, key, width) {
  if (!hasFullScope(scope)) return;
  const value = clampPaneWidth(width);
  if (null === value) return;
  Preferences.set(key, value, scope);
}

export function readBoardView(scope) {
  if (!hasFullScope(scope)) return BOARD_VIEWS[0];
  const stored = Preferences.get(BOARD_VIEW_KEY, scope);
  const view = LEGACY_BOARD_VIEWS[stored] || stored;
  return BOARD_VIEWS.includes(view) ? view : BOARD_VIEWS[0];
}

export function writeBoardView(scope, view) {
  if (!hasFullScope(scope) || !BOARD_VIEWS.includes(view)) return;
  Preferences.set(BOARD_VIEW_KEY, view, scope);
}
